<template>
  <TopLevelPageShell
    title="Account Settings"
    subtitle="Manage account-level publishing and feed settings."
  >
    <div class="text-subtitle-1 font-weight-bold mb-2">Publishing</div>
    <div class="text-body-2 text-grey mb-4">
      Control how automatic feed processing publishes inventory updates.
    </div>

    <v-card class="settings-panel-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Pause feed-triggered publishes
        </div>

        <p class="text-body-2 text-medium-emphasis mb-3">
          New feed uploads will still be ingested, but feed-triggered publishes will not publish
          inventory changes automatically.
        </p>

        <v-switch
          v-model="pausePublishByFeedLocal"
          label="Pause feed-triggered publishes"
          inset
          color="primary"
          hide-details
          data-testid="pause-publish-by-feed-switch"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            color="primary"
            variant="flat"
            :disabled="isSaving || !isPauseDirty"
            :loading="isSaving"
            data-testid="pause-publish-by-feed-save"
            @click="savePauseSetting"
          >
            Save
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="settings-panel-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">Chrome CVD language</div>

        <p class="text-body-2 text-medium-emphasis mb-3">
          Choose the language used for Chrome vehicle data.
        </p>

        <v-select
          v-model="chromeCvdLocaleLocal"
          :items="chromeCvdLocaleOptions"
          label="Chrome CVD language"
          hide-details
          style="max-width: 320px"
          data-testid="chrome-cvd-locale-select"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            color="primary"
            variant="flat"
            :disabled="isSavingChromeCvdLocale || !isChromeCvdLocaleDirty"
            :loading="isSavingChromeCvdLocale"
            data-testid="chrome-cvd-locale-save"
            @click="saveChromeCvdLocale"
          >
            Save
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="settings-panel-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Feed Guardrail — Large Drop Protection
        </div>

        <p class="text-body-2 text-medium-emphasis mb-3">
          Blocks automated feed publishes when a feed would cause a large inventory drop.
        </p>

        <v-switch
          v-model="guardrailEnabledLocal"
          label="Enable feed guardrail"
          inset
          color="primary"
          hide-details
          data-testid="feed-guardrail-enabled-switch"
        />

        <v-text-field
          v-model="guardrailThresholdLocal"
          type="number"
          label="Maximum allowed drop (%)"
          hint="If a feed would reduce inventory beyond this threshold, the publish is blocked and shown on the Dashboard for review."
          persistent-hint
          :min="1"
          :max="100"
          step="1"
          suffix="%"
          :disabled="!guardrailEnabledLocal"
          :error-messages="guardrailEnabledLocal ? guardrailThresholdError : ''"
          style="max-width: 320px"
          class="mt-3"
          data-testid="feed-guardrail-threshold-input"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!canSaveGuardrail"
            :loading="isSavingGuardrail"
            data-testid="feed-guardrail-save"
            @click="saveGuardrailSettings"
          >
            Save Guardrail Settings
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <template v-if="showInventoryAlertsCard">
      <div class="text-subtitle-1 font-weight-bold mt-6 mb-2">Inventory alerts</div>
      <div class="text-body-2 text-grey mb-4">
        Email or SMS staff when published manually added vehicles stay in stock past the expected
        time-to-sale.
      </div>

      <v-card class="settings-panel-card mb-4" elevation="0" data-testid="inventory-alerts-card">
        <v-card-text class="pa-4">
          <div class="text-subtitle-2 font-weight-bold mb-2">Manual vehicle aging</div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Email is on by default. SMS is opt-in and needs a phone number. If no recipients are
            saved, alerts fall back to the JAMS primary contact.
          </p>

          <v-switch
            v-model="inventoryAlertsLocal.enabled"
            label="Enable inventory alerts"
            inset
            color="primary"
            hide-details
            data-testid="inventory-alerts-enabled-switch"
          />

          <div class="d-flex flex-wrap ga-4 mt-3 mb-1">
            <v-checkbox
              v-model="inventoryAlertsEmailEnabled"
              label="Email"
              hide-details
              density="compact"
              :disabled="!inventoryAlertsLocal.enabled"
              data-testid="inventory-alerts-email-channel"
            />
            <v-checkbox
              v-model="inventoryAlertsSmsEnabled"
              label="SMS"
              hide-details
              density="compact"
              :disabled="!inventoryAlertsLocal.enabled"
              data-testid="inventory-alerts-sms-channel"
            />
          </div>
          <div v-if="inventoryAlertsChannelError" class="text-caption text-error mb-3">
            {{ inventoryAlertsChannelError }}
          </div>

          <div class="text-subtitle-2 font-weight-bold mt-4 mb-2">Recipients</div>
          <div
            v-for="(recipient, index) in inventoryAlertsLocal.recipients"
            :key="`inventory-alert-recipient-${index}`"
            class="d-flex flex-wrap ga-3 mb-2 align-start"
          >
            <v-text-field
              v-model="recipient.email"
              label="Email"
              type="email"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :disabled="!inventoryAlertsLocal.enabled"
              style="max-width: 280px"
              :data-testid="`inventory-alerts-recipient-email-${index}`"
            />
            <v-text-field
              v-model="recipient.phone"
              label="Phone"
              density="compact"
              variant="outlined"
              hide-details="auto"
              placeholder="+15551234567"
              :disabled="!inventoryAlertsLocal.enabled"
              style="max-width: 220px"
              :data-testid="`inventory-alerts-recipient-phone-${index}`"
            />
            <v-btn
              variant="text"
              color="error"
              :disabled="!inventoryAlertsLocal.enabled || inventoryAlertsLocal.recipients.length <= 1"
              :data-testid="`inventory-alerts-recipient-remove-${index}`"
              @click="removeInventoryAlertsRecipient(index)"
            >
              Remove
            </v-btn>
          </div>
          <v-btn
            variant="text"
            :disabled="!inventoryAlertsLocal.enabled"
            data-testid="inventory-alerts-recipient-add"
            @click="addInventoryAlertsRecipient"
          >
            Add recipient
          </v-btn>

          <div class="text-subtitle-2 font-weight-bold mt-5 mb-2">Aging thresholds</div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Alert when a published manual VIN exceeds these days in stock. Extra offsets send a
            follow-up after the first crossing.
          </p>

          <v-switch
            v-model="inventoryAlertsEvent.enabled"
            label="Alert when a manual vehicle exceeds time-to-sale"
            inset
            color="primary"
            hide-details
            :disabled="!inventoryAlertsLocal.enabled"
            data-testid="inventory-alerts-event-enabled-switch"
          />

          <div class="d-flex flex-wrap ga-3 mt-3">
            <v-text-field
              v-model.number="inventoryAlertsEvent.thresholdDaysByCondition.new"
              type="number"
              label="New (days)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :min="1"
              :disabled="!inventoryAlertsLocal.enabled || !inventoryAlertsEvent.enabled"
              style="max-width: 160px"
              data-testid="inventory-alerts-threshold-new"
            />
            <v-text-field
              v-model.number="inventoryAlertsEvent.thresholdDaysByCondition.used"
              type="number"
              label="Used (days)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :min="1"
              :disabled="!inventoryAlertsLocal.enabled || !inventoryAlertsEvent.enabled"
              style="max-width: 160px"
              data-testid="inventory-alerts-threshold-used"
            />
            <v-text-field
              v-model.number="inventoryAlertsEvent.thresholdDaysByCondition.cpo"
              type="number"
              label="CPO (days)"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :min="1"
              :disabled="!inventoryAlertsLocal.enabled || !inventoryAlertsEvent.enabled"
              style="max-width: 160px"
              data-testid="inventory-alerts-threshold-cpo"
            />
          </div>
          <div v-if="inventoryAlertsThresholdError" class="text-caption text-error mt-1">
            {{ inventoryAlertsThresholdError }}
          </div>

          <v-text-field
            v-model="inventoryAlertsOffsetsLocal"
            label="Follow-up offsets (days)"
            hint="Comma-separated extra days after the threshold. 0 is the first crossing."
            persistent-hint
            density="compact"
            variant="outlined"
            :disabled="!inventoryAlertsLocal.enabled || !inventoryAlertsEvent.enabled"
            :error-messages="inventoryAlertsOffsetsError"
            style="max-width: 360px"
            class="mt-4"
            data-testid="inventory-alerts-offsets"
          />

          <div class="d-flex justify-end mt-4">
            <v-btn
              color="primary"
              variant="flat"
              :disabled="!canSaveInventoryAlerts"
              :loading="isSavingInventoryAlerts"
              data-testid="inventory-alerts-save"
              @click="saveInventoryAlerts"
            >
              Save Inventory Alerts
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>
    <v-card class="settings-panel-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Ready Courtesy vehicle status
        </div>

        <p class="text-body-2 text-medium-emphasis mb-3">
          Show ready courtesy/FCTP vehicles in a separate “Ready Courtesy” inventory state.
          When disabled, they remain included in “Ready for Sale.”
        </p>

        <v-switch
          v-model="separateReadyCourtesyVehiclesLocal"
          label="Separate Ready Courtesy vehicles from Ready for Sale"
          inset
          color="primary"
          hide-details
          data-testid="separate-ready-courtesy-switch"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            color="primary"
            variant="flat"
            :disabled="isSavingReadyCourtesyState || !isReadyCourtesyStateDirty"
            :loading="isSavingReadyCourtesyState"
            data-testid="separate-ready-courtesy-save"
            @click="saveReadyCourtesyState"
          >
            Save
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <div class="text-subtitle-1 font-weight-bold mt-6 mb-2">Shopper SRP</div>
    <div class="text-body-2 text-grey mb-4">
      Configure how Vehicle Status options appear to shoppers. These settings do not change
      inventory lifecycle statuses.
    </div>

    <v-card class="settings-panel-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">Vehicle Status facet</div>
        <p class="text-body-2 text-medium-emphasis mb-3">
          Drag statuses into the desired display order and edit their shopper-facing labels.
          The underlying status values used for filtering remain unchanged.
        </p>

        <v-table density="comfortable" class="vehicle-status-table">
          <thead>
            <tr>
              <th class="drag-column" aria-label="Reorder"></th>
              <th>Status</th>
              <th>Shopper-facing label</th>
            </tr>
          </thead>
          <Draggable
            v-model="vehicleStatusFacetLocal"
            item-key="value"
            handle=".vehicle-status-drag-handle"
            tag="tbody"
            :animation="150"
            ghost-class="drag-ghost"
          >
            <template #item="{ element: option }">
              <tr
                v-show="
                  option.value !== 'READY_COURTESY' || separateReadyCourtesyVehiclesLocal
                "
                :data-testid="`vehicle-status-row-${option.value}`"
              >
                <td class="text-center">
                  <v-icon
                    icon="mdi-drag"
                    color="grey"
                    class="vehicle-status-drag-handle"
                    title="Drag to reorder"
                  />
                </td>
                <td>
                  <code>{{ option.value }}</code>
                </td>
                <td>
                  <v-text-field
                    v-model="option.label"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    maxlength="80"
                    :error-messages="option.label.trim() ? [] : ['Label is required']"
                    :data-testid="`vehicle-status-label-${option.value}`"
                  />
                </td>
              </tr>
            </template>
          </Draggable>
        </v-table>

        <div class="d-flex justify-space-between align-center mt-3">
          <v-btn
            variant="text"
            :disabled="isSavingVehicleStatusFacet"
            data-testid="vehicle-status-reset"
            @click="resetVehicleStatusFacet"
          >
            Reset defaults
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!canSaveVehicleStatusFacet"
            :loading="isSavingVehicleStatusFacet"
            data-testid="vehicle-status-save"
            @click="saveVehicleStatusFacet"
          >
            Save Vehicle Status Facet
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </TopLevelPageShell>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import TopLevelPageShell from '@/components/layout/TopLevelPageShell.vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import {
  defaultInventoryAlertsSettings,
  fetchIsePrimarySources,
  inventoryAlertsSettingsEqual,
  MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE,
  normalizeInventoryAlertsSettings,
  saveIseChromeCvdLocaleDraft,
  saveIseGuardrailSettingsDraft,
  saveIseInventoryAlertsDraft,
  saveIsePausePublishByFeedDraft,
  saveIseReadyCourtesyStateDraft,
  saveVehicleStatusFacetDraft,
  type ChromeCvdLocale,
  type InventoryAlertsEventSettings,
  type InventoryAlertsSettings,
  type VehicleStatusFacetOption,
} from '@/services/iseSettingsService'
import { makeAlert } from '@/utils'
import Draggable from 'vuedraggable'

const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()
const iseBackendUrl = import.meta.env.VITE_ISE_BACKEND_URL

const persistedPause = ref(false)
const pausePublishByFeedLocal = ref(false)
const isSaving = ref(false)

const chromeCvdLocaleOptions: Array<{ title: string; value: ChromeCvdLocale }> = [
  { title: 'English', value: 'en_US' },
  { title: 'Spanish', value: 'es_US' },
]
const persistedChromeCvdLocale = ref<ChromeCvdLocale>('en_US')
const chromeCvdLocaleLocal = ref<ChromeCvdLocale>('en_US')
const isSavingChromeCvdLocale = ref(false)

const persistedInventoryAlerts = ref<InventoryAlertsSettings>(defaultInventoryAlertsSettings())
const inventoryAlertsLocal = ref<InventoryAlertsSettings>({
  ...defaultInventoryAlertsSettings(),
  recipients: [{ email: '', phone: '' }],
})
const inventoryAlertsOffsetsLocal = ref('0, 10')
const inventoryAlertsProductAssigned = ref<boolean | null>(null)
const isSavingInventoryAlerts = ref(false)

const persistedGuardrailEnabled = ref(true)
const guardrailEnabledLocal = ref(true)
const persistedGuardrailThreshold = ref(50)
const guardrailThresholdLocal = ref<number | string>(50)
const isSavingGuardrail = ref(false)

const persistedSeparateReadyCourtesyVehicles = ref(false)
const separateReadyCourtesyVehiclesLocal = ref(false)
const isSavingReadyCourtesyState = ref(false)

const DEFAULT_VEHICLE_STATUS_FACET: readonly VehicleStatusFacetOption[] = [
  { value: 'IN_PRODUCTION', label: 'In Production' },
  { value: 'IN_SERVICE', label: 'In Service' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'ON_ORDER', label: 'On Order' },
  { value: 'READY_FOR_SALE', label: 'Ready for Sale' },
  { value: 'READY_COURTESY', label: 'Ready Courtesy' },
  { value: 'RESERVED', label: 'Reserved' },
]

function cloneVehicleStatusFacet(
  options: readonly VehicleStatusFacetOption[],
): VehicleStatusFacetOption[] {
  return options.map((option) => ({ ...option }))
}

function mergeVehicleStatusFacetDefaults(
  options: readonly VehicleStatusFacetOption[],
): VehicleStatusFacetOption[] {
  const merged = cloneVehicleStatusFacet(options)
  const existingValues = new Set(merged.map((option) => option.value))

  for (const defaultOption of DEFAULT_VEHICLE_STATUS_FACET) {
    if (!existingValues.has(defaultOption.value)) {
      merged.push({ ...defaultOption })
    }
  }

  return merged
}

const persistedVehicleStatusFacet = ref<VehicleStatusFacetOption[]>(
  cloneVehicleStatusFacet(DEFAULT_VEHICLE_STATUS_FACET),
)
const vehicleStatusFacetLocal = ref<VehicleStatusFacetOption[]>(
  cloneVehicleStatusFacet(DEFAULT_VEHICLE_STATUS_FACET),
)
const isSavingVehicleStatusFacet = ref(false)

const isPauseDirty = computed(() => pausePublishByFeedLocal.value !== persistedPause.value)
const isChromeCvdLocaleDirty = computed(
  () => chromeCvdLocaleLocal.value !== persistedChromeCvdLocale.value,
)
const isReadyCourtesyStateDirty = computed(
  () =>
    separateReadyCourtesyVehiclesLocal.value !==
    persistedSeparateReadyCourtesyVehicles.value,
)

const guardrailThresholdNumber = computed(() => Number(guardrailThresholdLocal.value))

const isGuardrailThresholdValid = computed(() => {
  const raw = String(guardrailThresholdLocal.value ?? '').trim()
  if (!raw) return false
  const n = Number(raw)
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1 && n <= 100
})

const guardrailThresholdError = computed(() =>
  isGuardrailThresholdValid.value ? '' : 'Enter a whole number from 1 to 100.',
)

const isGuardrailDirty = computed(() => {
  if (guardrailEnabledLocal.value !== persistedGuardrailEnabled.value) return true
  if (isGuardrailThresholdValid.value) {
    return guardrailThresholdNumber.value !== persistedGuardrailThreshold.value
  }
  return String(guardrailThresholdLocal.value ?? '').trim() !== String(persistedGuardrailThreshold.value)
})

const canSaveGuardrail = computed(
  () => isGuardrailDirty.value && isGuardrailThresholdValid.value && !isSavingGuardrail.value,
)

const isVehicleStatusFacetValid = computed(() =>
  vehicleStatusFacetLocal.value.every(
    (option) => option.label.trim().length > 0 && option.label.trim().length <= 80,
  ),
)
const isVehicleStatusFacetDirty = computed(
  () =>
    JSON.stringify(vehicleStatusFacetLocal.value) !==
    JSON.stringify(persistedVehicleStatusFacet.value),
)
const showInventoryAlertsCard = computed(() => inventoryAlertsProductAssigned.value !== false)

const inventoryAlertsEvent = computed<InventoryAlertsEventSettings>(() => {
  const event = inventoryAlertsLocal.value.events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]
  if (event) return event
  const fallback = defaultInventoryAlertsSettings().events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]
  inventoryAlertsLocal.value.events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE] = fallback
  return fallback
})

const inventoryAlertsEmailEnabled = computed({
  get: () => inventoryAlertsLocal.value.channels.includes('email'),
  set: (enabled: boolean) => {
    setInventoryAlertsChannel('email', enabled)
  },
})

const inventoryAlertsSmsEnabled = computed({
  get: () => inventoryAlertsLocal.value.channels.includes('sms'),
  set: (enabled: boolean) => {
    setInventoryAlertsChannel('sms', enabled)
  },
})

function setInventoryAlertsChannel(channel: 'email' | 'sms', enabled: boolean) {
  const channels = new Set(inventoryAlertsLocal.value.channels)
  if (enabled) channels.add(channel)
  else channels.delete(channel)
  inventoryAlertsLocal.value.channels = Array.from(channels).sort((a, b) => a.localeCompare(b))
}

function addInventoryAlertsRecipient() {
  inventoryAlertsLocal.value.recipients.push({ email: '', phone: '' })
}

function removeInventoryAlertsRecipient(index: number) {
  if (inventoryAlertsLocal.value.recipients.length <= 1) {
    inventoryAlertsLocal.value.recipients = [{ email: '', phone: '' }]
    return
  }
  inventoryAlertsLocal.value.recipients.splice(index, 1)
}

function parseInventoryAlertsOffsets(raw: string): number[] | null {
  const parts = String(raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length === 0) return null
  const numbers = parts.map((part) => Number(part))
  if (numbers.some((n) => !Number.isFinite(n) || n < 0 || !Number.isInteger(n))) return null
  return Array.from(new Set(numbers)).sort((a, b) => a - b)
}

const inventoryAlertsChannelError = computed(() =>
  inventoryAlertsLocal.value.channels.length > 0 ? '' : 'Select at least one channel.',
)

const inventoryAlertsThresholdError = computed(() => {
  const thresholds = inventoryAlertsEvent.value.thresholdDaysByCondition
  const values = [thresholds.new, thresholds.used, thresholds.cpo]
  if (values.every((value) => typeof value === 'number' && Number.isInteger(value) && value > 0)) {
    return ''
  }
  return 'Enter a whole number of days greater than 0 for New, Used, and CPO.'
})

const parsedInventoryAlertsOffsets = computed(() => parseInventoryAlertsOffsets(inventoryAlertsOffsetsLocal.value))

const inventoryAlertsOffsetsError = computed(() =>
  parsedInventoryAlertsOffsets.value ? '' : 'Enter comma-separated whole numbers 0 or greater.',
)

function buildInventoryAlertsFromForm(): InventoryAlertsSettings {
  const recipients = inventoryAlertsLocal.value.recipients
    .map((recipient) => ({
      ...(recipient.email?.trim() ? { email: recipient.email.trim() } : {}),
      ...(recipient.phone?.trim() ? { phone: recipient.phone.trim() } : {}),
    }))
    .filter((recipient) => recipient.email || recipient.phone)
  return normalizeInventoryAlertsSettings({
    enabled: inventoryAlertsLocal.value.enabled,
    channels: inventoryAlertsLocal.value.channels,
    recipients,
    events: {
      [MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE]: {
        ...inventoryAlertsEvent.value,
        offsetsDays: parsedInventoryAlertsOffsets.value ?? inventoryAlertsEvent.value.offsetsDays,
      },
    },
  })
}

function applyInventoryAlertsToForm(settings: InventoryAlertsSettings) {
  const next = normalizeInventoryAlertsSettings(settings)
  inventoryAlertsLocal.value = {
    ...next,
    recipients: next.recipients.length > 0 ? next.recipients.map((row) => ({ ...row })) : [{ email: '', phone: '' }],
  }
  inventoryAlertsOffsetsLocal.value = next.events[MANUAL_VEHICLE_EXCEEDED_TIME_TO_SALE].offsetsDays.join(', ')
}

const isInventoryAlertsDirty = computed(
  () => !inventoryAlertsSettingsEqual(buildInventoryAlertsFromForm(), persistedInventoryAlerts.value),
)

const isInventoryAlertsValid = computed(
  () =>
    !inventoryAlertsChannelError.value &&
    !inventoryAlertsThresholdError.value &&
    !inventoryAlertsOffsetsError.value,
)

const canSaveInventoryAlerts = computed(
  () => isInventoryAlertsDirty.value && isInventoryAlertsValid.value && !isSavingInventoryAlerts.value,
)

const canSaveVehicleStatusFacet = computed(
  () =>
    isVehicleStatusFacetDirty.value &&
    isVehicleStatusFacetValid.value &&
    !isSavingVehicleStatusFacet.value,
)

function normalizeGuardrailThreshold(raw: unknown): number {
  const n = Number(raw)
  if (!Number.isFinite(n)) return 50
  const rounded = Math.round(n)
  return rounded >= 1 && rounded <= 100 ? rounded : 50
}

async function loadPublishingSettings() {
  const endpoint = String(iseBackendUrl || '')
  const jazelId = String(accountSettings.accountId || '')
  const token = String(accountSettings.sessionToken || '')
  if (!endpoint || !jazelId || !token) return

  try {
    const res = await fetchIsePrimarySources(endpoint, jazelId, { includeDraft: true, token })

    const wasPauseDirty = isPauseDirty.value
    const wasChromeCvdLocaleDirty = isChromeCvdLocaleDirty.value
    const wasGuardrailDirty = isGuardrailDirty.value
    const wasReadyCourtesyStateDirty = isReadyCourtesyStateDirty.value
    const wasVehicleStatusFacetDirty = isVehicleStatusFacetDirty.value
    const wasInventoryAlertsDirty = isInventoryAlertsDirty.value

    inventoryAlertsProductAssigned.value =
      res?.inventoryAlertsProductAssigned === false ? false : true

    const pauseValue = !!res?.pause_publish_by_feed
    persistedPause.value = pauseValue
    if (!wasPauseDirty) pausePublishByFeedLocal.value = pauseValue

    const chromeCvdLocale = res?.chrome_cvd_locale === 'es_US' ? 'es_US' : 'en_US'
    persistedChromeCvdLocale.value = chromeCvdLocale
    if (!wasChromeCvdLocaleDirty) chromeCvdLocaleLocal.value = chromeCvdLocale

    const gEnabled = res?.feed_guardrail_enabled ?? true
    const gThreshold = normalizeGuardrailThreshold(res?.max_allowed_net_change_percent)
    persistedGuardrailEnabled.value = gEnabled
    persistedGuardrailThreshold.value = gThreshold
    if (!wasGuardrailDirty) {
      guardrailEnabledLocal.value = gEnabled
      guardrailThresholdLocal.value = gThreshold
    }

    const separateReadyCourtesyVehicles = !!res?.separate_ready_courtesy_vehicles
    persistedSeparateReadyCourtesyVehicles.value = separateReadyCourtesyVehicles
    if (!wasReadyCourtesyStateDirty) {
      separateReadyCourtesyVehiclesLocal.value = separateReadyCourtesyVehicles
    }

    const vehicleStatusFacet = mergeVehicleStatusFacetDefaults(
      Array.isArray(res?.vehicleStatusFacet) && res.vehicleStatusFacet.length > 0
        ? res.vehicleStatusFacet
        : DEFAULT_VEHICLE_STATUS_FACET,
    )
    persistedVehicleStatusFacet.value = cloneVehicleStatusFacet(vehicleStatusFacet)
    if (!wasVehicleStatusFacetDirty) {
      vehicleStatusFacetLocal.value = cloneVehicleStatusFacet(vehicleStatusFacet)
    }

    const inventoryAlerts = normalizeInventoryAlertsSettings(res?.inventoryAlerts)
    persistedInventoryAlerts.value = normalizeInventoryAlertsSettings(inventoryAlerts)
    if (!wasInventoryAlertsDirty) {
      applyInventoryAlertsToForm(inventoryAlerts)
    }
  } catch {
    // non-fatal: leave current UI state
  }
}

watch(
  () => [accountSettings.accountId, accountSettings.sessionToken],
  () => {
    void loadPublishingSettings()
  },
  { immediate: true },
)

async function savePauseSetting() {
  if (isSaving.value) return
  isSaving.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    await saveIsePausePublishByFeedDraft(endpoint, jazelId, pausePublishByFeedLocal.value, {
      token,
    })
    persistedPause.value = pausePublishByFeedLocal.value

    makeAlert('Pause setting saved as draft. Click "Publish Changes" to apply to live inventory.', 'success')
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'pausePublishByFeed' })
  } catch (error) {
    console.error('Failed to save pause setting', error)
    makeAlert('Failed to save pause setting. Please try again.', 'error')
  } finally {
    isSaving.value = false
  }
}

async function saveChromeCvdLocale() {
  if (isSavingChromeCvdLocale.value || !isChromeCvdLocaleDirty.value) return
  isSavingChromeCvdLocale.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    await saveIseChromeCvdLocaleDraft(endpoint, jazelId, chromeCvdLocaleLocal.value, { token })
    persistedChromeCvdLocale.value = chromeCvdLocaleLocal.value

    makeAlert('Chrome CVD language saved as draft. Click "Publish Changes" to apply.', 'success')
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'chromeCvdLocale' })
  } catch (error) {
    console.error('Failed to save Chrome CVD language', error)
    makeAlert('Failed to save Chrome CVD language. Please try again.', 'error')
  } finally {
    isSavingChromeCvdLocale.value = false
  }
}

async function saveGuardrailSettings() {
  if (isSavingGuardrail.value || !canSaveGuardrail.value) return
  isSavingGuardrail.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    const threshold = guardrailThresholdNumber.value
    await saveIseGuardrailSettingsDraft(
      endpoint,
      jazelId,
      {
        feed_guardrail_enabled: guardrailEnabledLocal.value,
        max_allowed_net_change_percent: threshold,
      },
      { token },
    )
    persistedGuardrailEnabled.value = guardrailEnabledLocal.value
    persistedGuardrailThreshold.value = threshold
    guardrailThresholdLocal.value = threshold

    makeAlert('Feed guardrail settings saved as draft. Click "Publish Changes" to apply.', 'success')
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'feedGuardrail' })
  } catch (error) {
    console.error('Failed to save feed guardrail settings', error)
    makeAlert('Failed to save feed guardrail settings. Please try again.', 'error')
  } finally {
    isSavingGuardrail.value = false
  }
}

async function saveInventoryAlerts() {
  if (!canSaveInventoryAlerts.value) return
  isSavingInventoryAlerts.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    const next = buildInventoryAlertsFromForm()
    await saveIseInventoryAlertsDraft(endpoint, jazelId, next, { token })
    persistedInventoryAlerts.value = normalizeInventoryAlertsSettings(next)
    applyInventoryAlertsToForm(next)

    makeAlert('Inventory alerts saved as draft. Click "Publish Changes" to apply.', 'success')
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'inventoryAlerts' })
  } catch (error) {
    console.error('Failed to save inventory alerts', error)
    makeAlert('Failed to save inventory alerts. Please try again.', 'error')
  } finally {
    isSavingInventoryAlerts.value = false
  }
}

async function saveReadyCourtesyState() {
  if (isSavingReadyCourtesyState.value || !isReadyCourtesyStateDirty.value) return
  isSavingReadyCourtesyState.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    await saveIseReadyCourtesyStateDraft(
      endpoint,
      jazelId,
      separateReadyCourtesyVehiclesLocal.value,
      { token },
    )
    persistedSeparateReadyCourtesyVehicles.value =
      separateReadyCourtesyVehiclesLocal.value

    makeAlert(
      'Ready Courtesy status setting saved as draft. Click "Publish Changes" to apply.',
      'success',
    )
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'readyCourtesyState' })
  } catch (error) {
    console.error('Failed to save Ready Courtesy state setting', error)
    makeAlert('Failed to save Ready Courtesy state setting. Please try again.', 'error')
  } finally {
    isSavingReadyCourtesyState.value = false
  }
}

function resetVehicleStatusFacet() {
  vehicleStatusFacetLocal.value = cloneVehicleStatusFacet(DEFAULT_VEHICLE_STATUS_FACET)
}

async function saveVehicleStatusFacet() {
  if (!canSaveVehicleStatusFacet.value) return
  isSavingVehicleStatusFacet.value = true
  try {
    const endpoint = String(iseBackendUrl || '')
    const jazelId = String(accountSettings.accountId || '')
    const token = String(accountSettings.sessionToken || '')
    if (!endpoint || !jazelId || !token) return

    const normalized = vehicleStatusFacetLocal.value.map((option) => ({
      value: option.value,
      label: option.label.trim(),
    }))
    await saveVehicleStatusFacetDraft(endpoint, jazelId, normalized, { token })
    persistedVehicleStatusFacet.value = cloneVehicleStatusFacet(normalized)
    vehicleStatusFacetLocal.value = cloneVehicleStatusFacet(normalized)

    makeAlert('Vehicle Status facet saved as draft. Click "Publish Changes" to apply.', 'success')
    await publishStore.checkAndReconcile(endpoint, jazelId)
    publishStore.markDirty('inventory', { source: 'vehicleStatusFacet' })
  } catch (error) {
    console.error('Failed to save Vehicle Status facet', error)
    makeAlert('Failed to save Vehicle Status facet. Please try again.', 'error')
  } finally {
    isSavingVehicleStatusFacet.value = false
  }
}
</script>

<style scoped>
.settings-panel-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}

.drag-column {
  width: 56px;
}

.vehicle-status-drag-handle {
  cursor: grab;
}

.vehicle-status-drag-handle:active {
  cursor: grabbing;
}

.vehicle-status-table code {
  white-space: nowrap;
}

.drag-ghost {
  opacity: 0.5;
}
</style>
